import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
  useFiltersQuery,
  useUpdateFilterMutation,
  useResetFilterMutation,
  useApplyPresetMutation,
  useCreatePresetMutation,
  useUpdatePresetMutation,
  useDeletePresetMutation,
  useSelectedPreset,
  useFilterStore,
} from '../services/filters'

import './FilterSystem.css'

/* =====================================================================
   EDGE Filter System — Toolbar (Preset-Dropdown + Pills) & Filter-Panel.
   UI nach dem Mockup "EDGE Filter System Re-Design"; Datenebene bleibt
   services/filters.js (current_filter / advanced_filter / current_presets).
   Smoothness: Mutationen patchen den ['filters']-Cache optimistisch,
   der Server-Reconcile laeuft unsichtbar hinterher.
   ===================================================================== */

const OP_LABELS = {
  '>=': 'mind.',
  '<=': 'max.',
  '>': 'größer',
  '<': 'kleiner',
  '=': 'gleich',
  '!=': 'ungleich',
  '∈': 'enthält',
  '∉': 'enthält nicht',
}

// Backend liefert keine Kategorien — Frontend-Mapping fuer die Library-Gruppen
const FILTER_CATEGORIES = {
  margin: 'Profit', roi: 'Profit', profit: 'Profit',
  margin30: 'Profit', roi30: 'Profit', profit30: 'Profit', margin_30d: 'Profit',
  salesrank: 'Sales', percent_bsr: 'Sales', top_bsr: 'Sales',
  confirmed_sales: 'Sales', estimated_sales: 'Sales', salesdrops: 'Sales', sales: 'Sales',
  sellprice: 'Preis', buyprice: 'Preis', price: 'Preis', discount: 'Preis',
  title: 'Produkt', category: 'Produkt', variations: 'Produkt', offers: 'Produkt',
  seller: 'Produkt', supplier: 'Produkt', shop: 'Produkt', brand: 'Produkt',
  country_blacklist: 'Produkt', pregen_list: 'Produkt', notification: 'Produkt',
}
const CAT_ORDER = ['Profit', 'Sales', 'Preis', 'Produkt', 'Weitere']
const categoryOf = (filterId) => FILTER_CATEGORIES[filterId] || 'Weitere'

export const sortKey = (item) => `${item.filter_id}-${item.filter_type || ''}`

const isNumericDef = (def) => def?.input_type === 'input_int'

// Anzeige-Label fuer Werte: request_value -> value aufloesen, Arrays kuerzen
export function valueLabel(item, def) {
  const raw = item?.attribute
  const resolve = (v) => def?.values?.find((o) => o.request_value === v)?.value ?? v
  if (Array.isArray(raw)) {
    if (!raw.length) return '–'
    const labels = raw.map(resolve)
    if (labels.length > 3) return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
    return labels.join(', ')
  }
  if (raw === '' || raw === null || raw === undefined) return '–'
  if (isNumericDef(def)) {
    const num = Number(raw)
    if (Number.isFinite(num)) {
      return `${num.toLocaleString('de-DE')}${def.unit ? ` ${def.unit}` : ''}`
    }
  }
  return String(resolve(raw))
}

function useClickOutside(open, onClose) {
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
  return ref
}

/* ---------- Gemeinsame Aktions-Schicht ----------
   Optimistik lebt KOMPLETT in services/filters.js (cancelQueries + Cache-Patch
   + Burst-Reconcile in den Mutationen). Hier NICHT zusaetzlich patchen: bei
   Array-Filtern toggelt der Mutations-Patch -- ein zweiter Patch davor wuerde
   den Wert sofort wieder ausschalten (an -> aus -> Server -> an = "laggy"). */
function useFilterActions(application, { updateData = true, onAnyChange } = {}) {
  const { mutateAsync: updateFilter } = useUpdateFilterMutation(application, updateData)
  const { mutateAsync: resetFilter } = useResetFilterMutation(application, updateData)

  const markChanged = useCallback(() => {
    onAnyChange?.()
  }, [onAnyChange])

  // Skalar (input_int / dropdown single): Wert setzen/ersetzen
  const setScalar = useCallback((filterId, filterType, value) => {
    markChanged()
    return updateFilter({ filter_id: filterId, filter_type: filterType, attribute: value })
  }, [markChanged, updateFilter])

  // Array (dropdown_multiple / input_list): einzelnen Wert togglen (Server- und
  // Cache-Semantik der Mutation). Letzter Wert raus -> resetFilter statt update,
  // damit serverseitig keine leere Zeile uebrig bleibt.
  const toggleArrayValue = useCallback((filterId, filterType, value, currentValues) => {
    const values = Array.isArray(currentValues) ? currentValues : []
    const isRemoval = values.includes(value)
    markChanged()
    if (isRemoval && values.length === 1) {
      return resetFilter({ filter_id: filterId, filter_type: filterType })
    }
    return updateFilter({ filter_id: filterId, filter_type: filterType, attribute: value })
  }, [markChanged, resetFilter, updateFilter])

  // Eine Bedingung (filter_id + filter_type) komplett entfernen
  const removeCondition = useCallback((filterId, filterType) => {
    markChanged()
    return resetFilter({ filter_id: filterId, filter_type: filterType })
  }, [markChanged, resetFilter])

  // Alle Bedingungen eines Filters entfernen
  const removeFilter = useCallback((filterId) => {
    markChanged()
    return resetFilter({ filter_id: filterId })
  }, [markChanged, resetFilter])

  // Alles leeren
  const clearAll = useCallback(() => {
    markChanged()
    return resetFilter()
  }, [markChanged, resetFilter])

  return { setScalar, toggleArrayValue, removeCondition, removeFilter, clearAll }
}

/* ---------- Condition segmented control ----------
   Nur fuer Drafts (noch ohne Wert): das Backend speichert Listen-Filter als
   eine Zeile PRO Wert mit filter_type im Key -- ein nachtraeglicher Typ-Wechsel
   waere Delete + Re-Insert jedes Wertes. Bewusste Entscheidung: einmal gesetzt
   = Typ fix (nur loeschen + neu anlegen), siehe OpStatic. */
function OpSegment({ types, value, onPick }) {
  if (!types?.length) return null
  return (
    <div className='fsys-op-seg'>
      {types.map((t) => (
        <button key={t} type='button' className={value === t ? 'on' : ''} onClick={() => t !== value && onPick(t)}>
          {t} <small>{OP_LABELS[t] || ''}</small>
        </button>
      ))}
    </div>
  )
}

/* Statische Operator-Anzeige fuer committete Bedingungen */
function OpStatic({ type }) {
  if (!type || type === 'null') return null
  return (
    <span className='fsys-op-static' title='Typ ist nach dem Setzen fix — Bedingung löschen und neu anlegen, um ihn zu ändern'>
      {type} <small>{OP_LABELS[type] || ''}</small>
    </span>
  )
}

/* ---------- Value-Editoren ---------- */
function NumberEditor({ item, def, onCommit, onClear, autoFocus }) {
  const committed = item.attribute === '' || item.attribute === null || item.attribute === undefined
    ? ''
    : String(item.attribute)
  const [draft, setDraft] = useState(committed)
  useEffect(() => { setDraft(committed) }, [committed])

  const commit = () => {
    const trimmed = draft.trim()
    if (trimmed === '') {
      if (committed !== '') onClear()
      return
    }
    if (trimmed !== committed) onCommit(trimmed)
  }

  return (
    <div className='fsys-val-num'>
      <input
        type='number'
        autoFocus={autoFocus}
        value={draft}
        placeholder='Wert'
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur() }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(committed) }
        }}
      />
      {def.unit && <span className='unit'>{def.unit}</span>}
    </div>
  )
}

function ChipsEditor({ item, def, onToggle, single }) {
  const values = Array.isArray(item.attribute)
    ? item.attribute
    : (item.attribute === '' || item.attribute === null || item.attribute === undefined ? [] : [item.attribute])
  return (
    <div className='fsys-val-chips'>
      {(def.values || []).map((o) => {
        const on = values.includes(o.request_value)
        return (
          <button
            key={o.request_value}
            type='button'
            className={on ? 'on' : ''}
            onClick={() => onToggle(o.request_value, values, { single, active: on })}
          >
            {o.value}
          </button>
        )
      })}
    </div>
  )
}

function TagsEditor({ item, onToggle, autoFocus }) {
  const tags = Array.isArray(item.attribute) ? item.attribute : []
  const [draft, setDraft] = useState('')
  const add = () => {
    const t = draft.trim()
    setDraft('')
    if (t && !tags.includes(t)) onToggle(t, tags)
  }
  return (
    <div className='fsys-val-tags'>
      {tags.map((t) => (
        <span className='tag' key={t}>
          <span className='t'>{t}</span>
          <button type='button' onClick={() => onToggle(t, tags)}>×</button>
        </span>
      ))}
      <input
        autoFocus={autoFocus}
        placeholder={tags.length ? '' : 'Keyword + Enter'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); add() }
        }}
        onBlur={add}
      />
    </div>
  )
}

function ValueEditor({ item, def, actions, autoFocus }) {
  if (def.input_type === 'dropdown' || def.input_type === 'dropdown_multiple') {
    const single = def.input_type === 'dropdown'
    return (
      <ChipsEditor
        item={item}
        def={def}
        single={single}
        onToggle={(value, currentValues, { active }) => {
          if (single) {
            if (active) {
              actions.removeCondition(item.filter_id, item.filter_type)
            } else {
              actions.setScalar(item.filter_id, item.filter_type, value)
            }
          } else {
            actions.toggleArrayValue(item.filter_id, item.filter_type, value, currentValues)
          }
        }}
      />
    )
  }
  if (def.input_type === 'input_list') {
    return (
      <TagsEditor
        item={item}
        autoFocus={autoFocus}
        onToggle={(value, currentValues) =>
          actions.toggleArrayValue(item.filter_id, item.filter_type, value, currentValues)}
      />
    )
  }
  return (
    <NumberEditor
      item={item}
      def={def}
      autoFocus={autoFocus}
      onCommit={(value) => actions.setScalar(item.filter_id, item.filter_type, value)}
      onClear={() => actions.removeCondition(item.filter_id, item.filter_type)}
    />
  )
}

/* ---------- Preset dropdown ---------- */
export function PresetDropdown({ application, compact, onAfterApply, deferDataReload = false }) {
  const { data } = useFiltersQuery(application)
  const queryClient = useQueryClient()
  const { selectedPresetId, setSelectedPresetId, isEdited } = useSelectedPreset(application)
  const { mutateAsync: applyPreset, isPending: isApplying } = useApplyPresetMutation(application, false)
  const { mutateAsync: updatePreset, isPending: isUpdating } = useUpdatePresetMutation(application, false)
  const { mutateAsync: createPreset, isPending: isCreating } = useCreatePresetMutation(application)
  const { mutateAsync: deletePreset } = useDeletePresetMutation(application)

  const [open, setOpen] = useState(false)
  const [confirmId, setConfirmId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState('')
  const [query, setQuery] = useState('')
  const [isSwitching, setIsSwitching] = useState(false)
  const [tip, setTip] = useState(null)
  const close = useCallback(() => setOpen(false), [])
  const ref = useClickOutside(open, close)

  useEffect(() => {
    if (!open) {
      setConfirmId(null)
      setCreating(false)
      setDraft('')
      setQuery('')
      setTip(null)
    }
  }, [open])

  // Presets stabil alphabetisch (Backend liefert ohne ORDER BY)
  const presets = useMemo(() =>
    Object.entries(data?.current_presets || {})
      .map(([id, entry]) => ({
        id,
        name: Array.isArray(entry) ? entry[0] : String(entry),
        filters: Array.isArray(entry) ? entry.slice(1) : [],
      }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' }))
  , [data?.current_presets])

  const selected = presets.find((p) => p.id === selectedPresetId) || null
  const defs = data?.filter?.advanced_filter || []
  const defOf = (filterId) => defs.find((d) => d.filter_id === filterId)

  // Update-Button waehrend Wechsel/Anwenden verstecken (Flacker-Guard wie zuvor)
  const showUpdate = Boolean(selected && (isEdited || isUpdating) && !isApplying && !isSwitching)

  const selectPreset = async (id) => {
    setOpen(false)
    // Im Defer-Modus (Panel) SOFORT markieren -- schliesst der User das Panel,
    // bevor der Apply-POST durch ist, soll der Close-Reload trotzdem feuern.
    if (deferDataReload) onAfterApply?.()
    setIsSwitching(true)
    setSelectedPresetId(id)
    try {
      await applyPreset(id)
    } finally {
      setIsSwitching(false)
    }
    // Toolbar: Daten sofort neu laden. Panel: Reload erst beim Schliessen.
    if (!deferDataReload) {
      queryClient.invalidateQueries({ queryKey: ['data', application] })
      onAfterApply?.()
    }
  }

  const submitNew = async () => {
    const name = draft.trim()
    setCreating(false)
    setDraft('')
    if (!name) return
    // Preset-Namen sind DB-seitig unique (pro User+Modul)
    if (presets.some((p) => String(p.name).toLowerCase() === name.toLowerCase())) {
      toast.error(`Preset "${name}" existiert bereits.`)
      return
    }
    setOpen(false)
    setSelectedPresetId(null)
    const preset = await createPreset({ preset_name: name })
    if (preset?.preset_id) {
      setSelectedPresetId(preset.preset_id)
    } else {
      toast.error(`Preset "${name}" konnte nicht erstellt werden.`)
    }
  }

  const handleDelete = (id) => {
    setConfirmId(null)
    if (selectedPresetId === id) setSelectedPresetId(null)
    deletePreset(id)
  }

  const showTip = (e, p) => {
    const r = e.currentTarget.getBoundingClientRect()
    const dd = ref.current.getBoundingClientRect()
    setTip({ preset: p, x: r.right - dd.left + 10, y: r.top - dd.top - 6 })
  }

  const q = query.trim().toLowerCase()
  const visible = q ? presets.filter((p) => String(p.name).toLowerCase().includes(q)) : presets

  return (
    <div className={`fsys-preset-dd ${compact ? 'compact' : ''}`} ref={ref}>
      <button
        type='button'
        className={`fsys-preset-trigger ${open ? 'open' : ''} ${selected ? 'has' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <i className='bx bx-bookmark'></i>
        <span className='nm'>{selected ? selected.name : 'Preset wählen'}</span>
        {selected && isEdited && !isSwitching && <span className='fsys-dot' title='Filter weichen vom Preset ab'></span>}
        <i className={`bx bx-chevron-down chev ${open ? 'up' : ''}`}></i>
      </button>
      {showUpdate && (
        <button
          type='button'
          className='fsys-preset-update'
          disabled={isUpdating}
          onClick={() => updatePreset(selectedPresetId)}
          title='Aktuelle Filter im Preset speichern'
        >
          {isUpdating && <i className='bx bx-loader-alt animate-spin' />}
          {isUpdating ? 'Speichert…' : 'Update'}
        </button>
      )}
      {open && (
        <div className='fsys-preset-menu'>
          {presets.length > 5 && (
            <div className='fsys-pm-search'>
              <i className='bx bx-search'></i>
              <input
                autoFocus
                placeholder='Preset suchen…'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          )}
          <div className='fsys-pm-list'>
            {visible.map((p) => {
              if (confirmId === p.id) {
                return (
                  <div className='fsys-pm-item confirm' key={p.id}>
                    <span className='q'>Preset wirklich löschen?</span>
                    <button type='button' className='yes' onClick={() => handleDelete(p.id)}>Ja</button>
                    <button type='button' className='no' onClick={() => setConfirmId(null)}>Nein</button>
                  </div>
                )
              }
              const on = p.id === selectedPresetId
              return (
                <div
                  className={`fsys-pm-item ${on ? 'on' : ''}`}
                  key={p.id}
                  onClick={() => { if (!on) selectPreset(p.id); else setOpen(false) }}
                  role='option'
                  aria-selected={on}
                  tabIndex={0}
                >
                  <span className='check'>{on ? '✓' : ''}</span>
                  <span className='nm'>
                    {p.name}
                    {on && isEdited && <span className='fsys-dot' title='Filter weichen vom Preset ab'></span>}
                  </span>
                  <span
                    className='meta'
                    onMouseEnter={(e) => showTip(e, p)}
                    onMouseLeave={() => setTip(null)}
                  >{p.filters.length} Filter</span>
                  <button
                    type='button'
                    className='fsys-pm-del'
                    title='Preset löschen'
                    onClick={(e) => { e.stopPropagation(); setConfirmId(p.id) }}
                  >×</button>
                </div>
              )
            })}
            {!visible.length && (
              <div className='fsys-pm-empty'>Kein Preset für „{query}“ gefunden.</div>
            )}
          </div>
          <div className='fsys-pm-foot'>
            {creating ? (
              <div className='fsys-pm-new-input'>
                <input
                  autoFocus
                  placeholder='Preset-Name…'
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitNew()
                    if (e.key === 'Escape') { setCreating(false); setDraft('') }
                  }}
                  onBlur={submitNew}
                />
              </div>
            ) : (
              <button type='button' className='fsys-pm-new' disabled={isCreating} onClick={() => setCreating(true)}>
                <i className='bx bx-plus'></i> Neues Preset aus aktuellen Filtern
              </button>
            )}
          </div>
        </div>
      )}
      {open && tip && (
        <div className='fsys-pm-tip' style={{ left: tip.x, top: tip.y }}>
          <div className='tip-h'>{tip.preset.name}</div>
          {tip.preset.filters.map((f, i) => {
            const def = defOf(f.filter_id)
            if (!def) return null
            return (
              <div className='tip-row' key={i}>
                <span className='k'>{def.name}</span>
                {f.filter_type && f.filter_type !== 'null' && <span className='op'>{f.filter_type}</span>}
                <span className='v'>{valueLabel(f, def)}</span>
              </div>
            )
          })}
          {!tip.preset.filters.length && (
            <div className='tip-row'><span className='v'>Keine Filter</span></div>
          )}
        </div>
      )}
    </div>
  )
}

/* ---------- Filter-Pill (Toolbar) mit Edit-Popover ---------- */
function FilterPill({ item, def, actions }) {
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const ref = useClickOutside(open, close)

  const hasOps = Array.isArray(def.allowed_filter_types) && def.allowed_filter_types.length > 0

  return (
    <span style={{ position: 'relative', display: 'inline-flex', maxWidth: '100%' }} ref={ref}>
      <span className={`fsys-f-pill ${open ? 'open' : ''}`}>
        <span className='main' onClick={() => setOpen((o) => !o)}>
          <span className='k'>{def.name}</span>
          {hasOps && item.filter_type !== 'null' && <span className='op'>{item.filter_type}</span>}
          <span className='v'>{valueLabel(item, def)}</span>
        </span>
        <button
          type='button'
          className='x'
          title='Filter entfernen'
          onClick={() => actions.removeCondition(item.filter_id, item.filter_type)}
        >×</button>
      </span>
      {open && (
        <div className='fsys-pill-pop' style={{ top: 'calc(100% + 6px)', left: 0 }}>
          <div className='head'>
            <span className='nm'>{def.name}</span>
            <button
              type='button'
              className='rm'
              onClick={() => { setOpen(false); actions.removeCondition(item.filter_id, item.filter_type) }}
            >Entfernen</button>
          </div>
          {hasOps && <div><OpStatic type={item.filter_type} /></div>}
          <ValueEditor item={item} def={def} actions={actions} autoFocus={isNumericDef(def)} />
        </div>
      )}
    </span>
  )
}

/* ---------- Filter-Panel (ersetzt das alte Scan-settings-Modal) ---------- */
function FilterPanel({ open, onClose, application, supplierMode, defs, serverItems, actions, onPresetApplied }) {
  const [query, setQuery] = useState('')
  // Drafts: im Panel hinzugefuegte Bedingungen ohne Wert — existieren nur lokal,
  // bis ein Wert gesetzt wird (dann legt der Server sie in current_filter an).
  const [drafts, setDrafts] = useState([])
  // Inline-Bestaetigung fuer "Alle Filter löschen" (gleiches System wie das
  // Preset-Löschen: erst "Wirklich? Ja/Nein", dann ausgefuehrt).
  const [confirmClear, setConfirmClear] = useState(false)

  // Stabile Anzeige-Reihenfolge, solange das Panel offen ist: sortKeys in
  // Erst-Erscheinungs-Reihenfolge. Ohne das springt ein Draft beim ersten
  // committeten Wert von seiner Add-Position an die server-sortierte Stelle.
  // Beim naechsten Oeffnen wird frisch (server-sortiert) initialisiert.
  const rowOrderRef = useRef([])
  const wasOpenRef = useRef(false)
  const activeScrollRef = useRef(null)
  if (open && !wasOpenRef.current) {
    rowOrderRef.current = []
  }
  wasOpenRef.current = open

  useEffect(() => {
    if (open) {
      setQuery('')
      setDrafts([])
      setConfirmClear(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Server-Eintraege verdraengen gleichnamige Drafts
  const merged = useMemo(() => {
    const shadowed = new Set(serverItems.map(sortKey))
    return [...serverItems, ...drafts.filter((d) => !shadowed.has(sortKey(d)))]
  }, [drafts, serverItems])

  // merged in stabiler Erst-Erscheinungs-Reihenfolge. Neue Keys werden hinten
  // an rowOrderRef angehaengt (append-only -> idempotent bei Memo-Reruns);
  // verschwundene Keys bleiben harmlos in der Liste stehen.
  const orderedMerged = useMemo(() => {
    if (!open) return []
    const order = rowOrderRef.current
    const byKey = new Map(merged.map((f) => [sortKey(f), f]))
    const result = []
    for (const key of order) {
      const f = byKey.get(key)
      if (f) {
        result.push(f)
        byKey.delete(key)
      }
    }
    for (const f of merged) {
      const key = sortKey(f)
      if (byKey.has(key)) {
        result.push(f)
        order.push(key)
        byKey.delete(key)
      }
    }
    return result
  }, [merged, open])

  const draftAwareActions = useMemo(() => ({
    ...actions,
    removeCondition: (filterId, filterType) => {
      const isDraft = !serverItems.some((i) => i.filter_id === filterId && i.filter_type === filterType)
      setDrafts((ds) => ds.filter((d) => !(d.filter_id === filterId && d.filter_type === filterType)))
      if (!isDraft) actions.removeCondition(filterId, filterType)
    },
    removeFilter: (filterId) => {
      setDrafts((ds) => ds.filter((d) => d.filter_id !== filterId))
      if (serverItems.some((i) => i.filter_id === filterId)) actions.removeFilter(filterId)
    },
  }), [actions, serverItems])

  // Typ-Wechsel gibt es nur fuer Drafts (rein lokal, noch kein Server-Eintrag).
  // Committete Bedingungen zeigen OpStatic — Typ ist nach dem Setzen fix.
  const retypeDraft = useCallback((item, newType) => {
    // Position in der stabilen Reihenfolge behalten: alter Key -> neuer Key in-place
    const oldKey = sortKey(item)
    const newKey = sortKey({ ...item, filter_type: newType })
    const idx = rowOrderRef.current.indexOf(oldKey)
    if (idx !== -1) rowOrderRef.current[idx] = newKey
    setDrafts((ds) => ds.map((d) =>
      d.filter_id === item.filter_id && d.filter_type === item.filter_type
        ? { ...d, filter_type: newType }
        : d
    ))
  }, [])

  if (!open) return null

  const visibleDefs = defs.filter((d) => !d.b2b_only || supplierMode)
  const q = query.trim().toLowerCase()
  const searched = q ? visibleDefs.filter((d) => String(d.name).toLowerCase().includes(q)) : visibleDefs
  const grouped = CAT_ORDER
    .map((cat) => ({ cat, items: searched.filter((d) => categoryOf(d.filter_id) === cat) }))
    .filter((g) => g.items.length)

  const activeIds = []
  for (const f of orderedMerged) if (!activeIds.includes(f.filter_id)) activeIds.push(f.filter_id)

  const addFilter = (def, type) => {
    const usedTypes = merged.filter((f) => f.filter_id === def.filter_id).map((f) => f.filter_type)
    const nextType = type
      || (def.allowed_filter_types || []).find((t) => !usedTypes.includes(t))
      || (def.allowed_filter_types ? null : 'null')
    if (!nextType) return
    setDrafts((ds) => [...ds, {
      filter_id: def.filter_id,
      filter_type: nextType,
      attribute: (def.input_type === 'dropdown_multiple' || def.input_type === 'input_list') ? [] : '',
    }])
    // Neue Karte (bzw. bestehende Karte bei weiterer Bedingung) in den Blick
    // scrollen -- neue Filter haengen immer unten an (rowOrderRef), der User
    // soll nicht danach suchen muessen. rAF: erst nach dem Commit/Paint
    // existiert das Karten-Element.
    requestAnimationFrame(() => {
      const scroller = activeScrollRef.current
      if (!scroller) return
      const card = scroller.querySelector(`[data-fsys-card="${def.filter_id}"]`)
      if (card) {
        card.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else {
        scroller.scrollTop = scroller.scrollHeight
      }
    })
  }

  // Portal an document.body: die Toolbar steckt z.B. im Wholesale-Subheader in
  // einem Stacking-Context -- inline gerendert waere das z-400 des Scrims darin
  // gefangen und z.B. die Pagination-Bar malt sich ueber das Panel.
  return createPortal(
    <div className='fsys fsys-scrim' onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className='fsys-panel'>
        <div className='fsys-panel-head'>
          <span className='ttl'>Filter <em>& Presets</em></span>
          <PresetDropdown application={application} compact deferDataReload onAfterApply={onPresetApplied} />
          <span className='spacer'></span>
          <button type='button' className='close' onClick={onClose} title='Schließen (Esc)'><i className='bx bx-x'></i></button>
        </div>

        <div className='fsys-panel-body'>
          {/* Library */}
          <div className='fsys-lib'>
            <div className='search'>
              <i className='bx bx-search'></i>
              <input
                placeholder='Filter suchen…'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className='groups'>
              {grouped.map((g) => (
                <div key={g.cat}>
                  <div className='group-h'>{g.cat}</div>
                  {g.items.map((d) => {
                    // Haken sobald der Filter UEBERHAUPT aktiv ist (wie im Mockup) --
                    // nicht erst, wenn alle Operator-Typen belegt sind. Sonst zeigt
                    // der erste Klick bei mind./max.-Filtern kein Feedback und
                    // Doppelklicks legen unbemerkt zwei Bedingungen an. Der Klick
                    // ist rein lokal (Draft), es gibt hier keinen Backend-Roundtrip.
                    const added = activeIds.includes(d.filter_id)
                    return (
                      <button
                        key={d.filter_id}
                        type='button'
                        className={`fsys-lib-item ${added ? 'added' : ''}`}
                        onClick={() => {
                          if (added) {
                            // Schon aktiv -> zur Karte scrollen statt still eine
                            // weitere Bedingung anzulegen (dafuer gibt es
                            // "+ Weitere Bedingung" in der Karte).
                            activeScrollRef.current
                              ?.querySelector(`[data-fsys-card="${d.filter_id}"]`)
                              ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
                            return
                          }
                          addFilter(d)
                        }}
                      >
                        {d.name}
                        {d.b2b_only && <span className='b2b'>B2B</span>}
                        <span className='plus'>+</span>
                      </button>
                    )
                  })}
                </div>
              ))}
              {!grouped.length && (
                <div className='fsys-lib-empty'>Kein Filter für „{query}“ gefunden.</div>
              )}
            </div>
          </div>

          {/* Active filters */}
          <div className='fsys-active-col'>
            {activeIds.length === 0 ? (
              <div className='fsys-active-empty'>
                <span className='big'>🎯</span>
                <b>Noch keine Filter aktiv</b>
                <span>Wähl links einen Filter aus der Bibliothek — oder lade eins deiner Presets oben.</span>
              </div>
            ) : (
              <div className='fsys-active-scroll' ref={activeScrollRef}>
                {activeIds.map((filterId) => {
                  const def = defs.find((d) => d.filter_id === filterId)
                  if (!def) return null
                  const rows = orderedMerged.filter((f) => f.filter_id === filterId)
                  const usedTypes = rows.map((f) => f.filter_type)
                  const remaining = (def.allowed_filter_types || []).filter((t) => !usedTypes.includes(t))
                  return (
                    <div className='fsys-af-card' key={filterId} data-fsys-card={filterId}>
                      <div className='head'>
                        <span className='nm'>{def.name}</span>
                        <span className='cat'>{categoryOf(filterId)}</span>
                        <button
                          type='button'
                          className='rm'
                          title='Filter komplett entfernen'
                          onClick={() => draftAwareActions.removeFilter(filterId)}
                        >×</button>
                      </div>
                      {rows.map((f) => {
                        const isDraft = !serverItems.some((i) => sortKey(i) === sortKey(f))
                        return (
                        <div className='fsys-af-row' key={sortKey(f)}>
                          {def.allowed_filter_types && f.filter_type !== 'null' && (
                            isDraft
                              ? <OpSegment
                                  types={[f.filter_type, ...remaining]}
                                  value={f.filter_type}
                                  onPick={(t) => retypeDraft(f, t)}
                                />
                              : <OpStatic type={f.filter_type} />
                          )}
                          <div className='val'>
                            <ValueEditor item={f} def={def} actions={draftAwareActions} />
                          </div>
                          {rows.length > 1 && (
                            <button
                              type='button'
                              className='rm-row'
                              title='Bedingung entfernen'
                              onClick={() => draftAwareActions.removeCondition(f.filter_id, f.filter_type)}
                            >×</button>
                          )}
                        </div>
                        )
                      })}
                      {remaining.length > 0 && (
                        <button type='button' className='more' onClick={() => addFilter(def, remaining[0])}>
                          + Weitere Bedingung
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <div className='fsys-panel-foot'>
              <span className='spacer'></span>
              {merged.length > 0 && (
                confirmClear ? (
                  <span className='confirm'>
                    <span className='q'>Wirklich alle löschen?</span>
                    <button
                      type='button'
                      className='yes'
                      onClick={() => { setConfirmClear(false); setDrafts([]); actions.clearAll() }}
                    >Ja</button>
                    <button
                      type='button'
                      className='no'
                      onClick={() => setConfirmClear(false)}
                    >Nein</button>
                  </span>
                ) : (
                  <button
                    type='button'
                    className='ghost'
                    onClick={() => setConfirmClear(true)}
                  >Alle Filter löschen</button>
                )
              )}
              <button type='button' className='apply' onClick={onClose}>
                Fertig
                <i className='bx bx-check' style={{ fontSize: 18 }}></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ---------- Toolbar (Default-Export, gleiche Props-API wie zuvor) ---------- */
export default function Filters({ onChange, application, supplierMode = false }) {
  const { data, isFetching } = useFiltersQuery(application)
  const setFilterPanelOpen = useFilterStore((s) => s.setFilterPanelOpen)
  const queryClient = useQueryClient()
  const [panelOpen, setPanelOpen] = useState(false)
  const panelChangedRef = useRef(false)

  // Safety: bei Unmount/Modulwechsel den Panel-Hold loesen, sonst bleibt die
  // Daten-Query des Grids dauerhaft pausiert.
  useEffect(() => () => setFilterPanelOpen(application, false), [application, setFilterPanelOpen])

  // Toolbar-Edits laden die Daten sofort neu; Panel-Edits werden gebatcht
  // (updateData=false) und beim Schliessen einmal invalidiert.
  const toolbarActions = useFilterActions(application, { updateData: true, onAnyChange: onChange })
  const panelActions = useFilterActions(application, {
    updateData: false,
    onAnyChange: () => { panelChangedRef.current = true },
  })

  const defs = useMemo(() => data?.filter?.advanced_filter || [], [data?.filter?.advanced_filter])
  const filtersReady = defs.length > 0
  const isFiltersLoading = !filtersReady && isFetching

  // Pills deterministisch sortieren (Definitions-Reihenfolge, dann filter_type):
  // das Backend liefert current_filter ohne ORDER BY.
  const items = useMemo(() => {
    const order = new Map(defs.map((def, index) => [def.filter_id, index]))
    return (data?.current_filter || [])
      .filter((item) => {
        const def = defs.find((d) => d.filter_id === item.filter_id)
        return def && (!def.b2b_only || supplierMode)
      })
      .sort((a, b) =>
        (order.get(a.filter_id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.filter_id) ?? Number.MAX_SAFE_INTEGER) ||
        String(a.filter_type).localeCompare(String(b.filter_type))
      )
  }, [data?.current_filter, defs, supplierMode])

  const handlePanelClose = useCallback(() => {
    setPanelOpen(false)
    // Hold zuerst loesen: das Grid wendet dann seinen aufgeschobenen
    // pageIndex-Reset an und laedt genau EINMAL mit dem neuen Filterstand.
    setFilterPanelOpen(application, false)
    if (panelChangedRef.current) {
      panelChangedRef.current = false
      queryClient.invalidateQueries({ queryKey: ['data', application] })
      onChange?.()
    }
  }, [application, onChange, queryClient, setFilterPanelOpen])

  return (
    <div className='fsys fsys-pill-row'>
      <PresetDropdown
        application={application}
        onAfterApply={onChange}
      />
      <span className='fsys-pill-sep'></span>

      {items.map((item) => {
        const def = defs.find((d) => d.filter_id === item.filter_id)
        return <FilterPill key={sortKey(item)} item={item} def={def} actions={toolbarActions} />
      })}

      <button
        type='button'
        className='fsys-add-filter'
        disabled={isFiltersLoading}
        onClick={() => {
          // Bewusst KEIN cancelQueries hier: ein laufender Produkt-Fetch laeuft
          // zu Ende, fuellt den Cache und darf hinterm Panel sichtbar fertig
          // werden (Live-Anzeige). Cancel+Restart hiess: Server rechnet umsonst
          // + neuer Request beim Schliessen (Spam bei Open/Close-Zyklen).
          setFilterPanelOpen(application, true)
          setPanelOpen(true)
        }}
      >
        {isFiltersLoading
          ? <><i className='bx bx-loader-alt animate-spin'></i> Filter laden…</>
          : <><i className='bx bx-slider-alt' style={{ fontSize: 15 }}></i> Filter</>}
      </button>
      <FilterPanel
        open={panelOpen}
        onClose={handlePanelClose}
        application={application}
        supplierMode={supplierMode}
        defs={defs}
        serverItems={items}
        actions={panelActions}
        onPresetApplied={() => { panelChangedRef.current = true }}
      />
    </div>
  )
}
